// Loan Officers Configuration
// Each officer has a unique code for authentication
// Template access permissions control which loan types each user can see

// Define available loan types
export const TEMPLATE_TYPES = {
  CONSTRUCTION: 'construction',
  BRIDGE: 'bridge',
  PORTFOLIO: 'portfolio',
  CHURCHILL_DSCR: 'churchill_dscr',
  IOI: 'ioi',
};

// All templates array for full access users (includes Construction)
export const ALL_TEMPLATES = [
  TEMPLATE_TYPES.CONSTRUCTION,
  TEMPLATE_TYPES.BRIDGE,
  TEMPLATE_TYPES.PORTFOLIO,
  TEMPLATE_TYPES.CHURCHILL_DSCR,
  TEMPLATE_TYPES.IOI,
];

// Templates without Construction (for most users)
export const STANDARD_TEMPLATES = [
  TEMPLATE_TYPES.BRIDGE,
  TEMPLATE_TYPES.PORTFOLIO,
  TEMPLATE_TYPES.IOI,
];

export const LOAN_OFFICERS = {
  // George Fraguio - VP of Origination (Construction, Bridge, Portfolio — no Churchill)
  george: {
    code: 'george3517',
    name: 'George Fraguio',
    title: 'VP of Origination',
    nmls: '1243940',
    email: 'gfraguio@vaster.com',
    applicationLink: 'www.vaster.app/new/gfraguio@vaster.com',
    phone: '305-742-4931',
    isAdmin: false,
    allowedTemplates: [
      TEMPLATE_TYPES.CONSTRUCTION,
      TEMPLATE_TYPES.BRIDGE,
      TEMPLATE_TYPES.PORTFOLIO,
      TEMPLATE_TYPES.IOI,
    ],
  },
  
  // Zach Simkins - Bridge, Portfolio, Churchill (No Construction)
  zach: {
    code: 'zach7462',
    name: 'Zach Simkins',
    title: 'Loan Originator',
    nmls: null,
    email: 'zsimkins@vaster.com',
    applicationLink: 'www.vaster.app/new/zsimkins@vaster.com',
    phone: null,
    isAdmin: false,
    allowedTemplates: STANDARD_TEMPLATES,
  },
  
  // Marcela Ozan - All Templates including Construction
  marcela: {
    code: 'marcela2847',
    name: 'Marcela Ozan',
    title: 'Origination Associate',
    nmls: null,
    email: 'mozan@vaster.com',
    applicationLink: 'www.vaster.app/new/mozan@vaster.com',
    phone: '941-539-2937',
    isAdmin: false,
    allowedTemplates: ALL_TEMPLATES,
  },
  
  // Rafael Dasso - Bridge, Portfolio, Churchill (No Construction)
  rafael: {
    code: 'rafael2842',
    name: 'Rafael Dasso',
    title: 'Loan Originator',
    nmls: '2842273',
    email: 'Rdasso@vaster.com',
    phone: '786-931-6419',
    isAdmin: false,
    allowedTemplates: STANDARD_TEMPLATES,
  },
  
  // Vincent Diaz - Bridge, Portfolio (No Construction)
  vince: {
    code: 'vince4829',
    name: 'Vincent Diaz',
    title: 'Loan Originator',
    nmls: null,
    email: 'vdiaz@vaster.com',
    applicationLink: 'www.vaster.app/new/vdiaz@vaster.com',
    phone: '305-389-2211',
    isAdmin: false,
    allowedTemplates: [
      TEMPLATE_TYPES.BRIDGE,
      TEMPLATE_TYPES.PORTFOLIO,
      TEMPLATE_TYPES.IOI,
    ],
  },
  
  // Agostina Tizzano - Bridge, Portfolio/Developer
  agos: {
    code: 'agos9183',
    name: 'Agostina Tizzano',
    title: 'Loan Originator',
    nmls: '1764154',
    email: 'atizzano@vaster.com',
    applicationLink: 'www.vaster.app/new/atizzano@vaster.com',
    phone: '305-709-9805',
    isAdmin: false,
    allowedTemplates: [
      TEMPLATE_TYPES.BRIDGE,
      TEMPLATE_TYPES.PORTFOLIO,
      TEMPLATE_TYPES.IOI,
    ],
  },
  
  // Carmen Stevens - Bridge, Portfolio/Developer
  carmen: {
    code: 'Vaster123',
    name: 'Carmen Stevens',
    title: 'VP of Portfolio Lending',
    nmls: '347931',
    email: 'cstevens@vaster.com',
    applicationLink: 'www.vaster.app/new/cstevens@vaster.com',
    phone: '305-778-1040',
    isAdmin: false,
    allowedTemplates: [
      TEMPLATE_TYPES.BRIDGE,
      TEMPLATE_TYPES.PORTFOLIO,
      TEMPLATE_TYPES.IOI,
    ],
  },
  
  // Bryan Capriles - Bridge, Portfolio (No Construction)
  bryan: {
    code: 'bryan6204',
    name: 'Bryan Capriles',
    title: 'Loan Originator',
    nmls: '1702318',
    email: 'bcapriles@vaster.com',
    applicationLink: 'www.vaster.app/new/bcapriles@vaster.com',
    phone: '831-295-2302',
    isAdmin: false,
    allowedTemplates: STANDARD_TEMPLATES,
  },
  
  // General/Shared Login - Bridge, Portfolio, Churchill (No Construction)
  general: {
    code: 'vaster2024',
    name: 'Vaster Team',
    title: 'Shared Access',
    nmls: null,
    email: 'info@vaster.com',
    phone: '305-209-5765',
    isAdmin: false,
    allowedTemplates: STANDARD_TEMPLATES,
  },
};

// Validate officer code and return officer data if valid
export const authenticateOfficer = (code) => {
  const lowerCode = code?.toLowerCase()?.trim();
  
  for (const [key, officer] of Object.entries(LOAN_OFFICERS)) {
    if (officer.code.toLowerCase() === lowerCode) {
      return { ...officer, id: key };
    }
  }
  
  return null;
};

// Check if user has access to a specific template
export const hasTemplateAccess = (officer, templateType) => {
  if (!officer || !officer.allowedTemplates) return false;
  return officer.allowedTemplates.includes(templateType);
};

// Get allowed templates for an officer
export const getAllowedTemplates = (officer) => {
  if (!officer || !officer.allowedTemplates) return [];
  return officer.allowedTemplates;
};

// Check if user is admin (can upload rate sheets)
export const isAdmin = (officer) => {
  return officer?.isAdmin === true;
};

// Get all officer names for display (without sensitive data)
export const getOfficerNames = () => {
  return Object.entries(LOAN_OFFICERS).map(([key, officer]) => ({
    id: key,
    name: officer.name,
  }));
};
